import { Box, Flex, useColorModeValue } from "@chakra-ui/react";
import { ErrorState } from "../../../../components/ErrorState";
import { StatsGrid, type StatKey } from "./StatsGrid";
import { CandidateListToolbar } from "./CandidateListToolbar";
import { CandidateSelectAll } from "./CandidateSelectAll";
import { Kanban } from "./Kanban";
import type { KanbanColumnData } from "./KanbanColumn";
import { CandidateSectionAccordion, type SectionGroup } from "./CandidateSectionAccordion";
import type { ViewMode } from "./ViewToggle";
import type { Candidate, OnboardingStats } from "../../../../types/onboarding";

interface MapaCandidatosTabProps {
  stats: OnboardingStats;
  isLoading: boolean;
  error: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  selectedCandidateIds: Set<string | number>;
  onToggleSelect: (id: string | number) => void;
  onSelectAll: () => void;
  candidates: Candidate[];
  columns: KanbanColumnData[];
  sectionGroups: SectionGroup[];
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onSectionExpand: (key: string) => void;
  statusFilter: StatKey[];
  onStatusFilterToggle: (key: StatKey) => void;
  onViewDetail: (c: Candidate) => void;
  onCancel: (c: Candidate) => void;
  onReactivate: (c: Candidate) => void;
  onDownload: () => void;
  onExport: () => void;
  onRetry: () => void;
}

export const MapaCandidatosTab = ({
  stats, isLoading, error, search, onSearchChange,
  selectedCandidateIds, onToggleSelect, onSelectAll,
  candidates, columns, sectionGroups, viewMode, onViewModeChange, onSectionExpand,
  statusFilter, onStatusFilterToggle,
  onViewDetail, onCancel, onReactivate,
  onDownload, onExport, onRetry,
}: MapaCandidatosTabProps) => {
  const dividerColor = useColorModeValue("gray.100", "whiteAlpha.80");

  return (
    <Flex direction="column" h="calc(100vh - 160px)" minH="400px">
      <Box flexShrink={0}>
        <StatsGrid
          stats={stats} isLoading={isLoading}
          showOnly={["aguardando", "em_andamento", "aguardando_rh", "concluidos", "cancelados"]}
          activeKey={statusFilter.length === 1 ? statusFilter[0] : null}
          onCardClick={onStatusFilterToggle}
        />

        <CandidateListToolbar
          search={search} onSearchChange={onSearchChange}
          selectedCount={selectedCandidateIds.size}
          onDownload={onDownload} onExport={onExport}
          viewMode={viewMode} onViewModeChange={onViewModeChange}
        />

        <Box borderTopWidth="1px" borderColor={dividerColor} mb={4} />

        {candidates.length > 0 && (
          <CandidateSelectAll
            selectedCount={selectedCandidateIds.size}
            totalCount={candidates.length}
            onToggle={onSelectAll}
          />
        )}
      </Box>

      {error ? (
        <ErrorState onRetry={onRetry} />
      ) : viewMode === "kanban" ? (
        <Box flex={1} overflow="hidden" minH={0}>
          <Kanban
            columns={columns}
            selectedCandidateIds={selectedCandidateIds}
            onToggleSelect={onToggleSelect}
            onViewDetail={onViewDetail}
            onCancel={onCancel}
            onReactivate={onReactivate}
          />
        </Box>
      ) : (
        <CandidateSectionAccordion
          sections={sectionGroups}
          defaultOpenKey="aguardando_rh"
          onExpand={onSectionExpand}
          selectedCandidateIds={selectedCandidateIds}
          onToggleSelect={onToggleSelect}
          onViewDetail={onViewDetail}
          onCancel={onCancel}
          onReactivate={onReactivate}
        />
      )}
    </Flex>
  );
};
